/// Thunderstorm model and lifecycle, ported from iOS
/// `Models/DifficultyModels.swift:94-120` and `Models/GameViewModel.swift:3644-3844`.
///
/// A storm is a circular area-of-effect that drifts over the radar.
/// Three phases over a fixed lifespan: forming (0–20%) → mature
/// (20–80%) → dissipating (80–100%). Size and speed lerp during the
/// outer two phases; mature is full base values.
///
/// All time-keeping is in *sim seconds* — the engine is fed
/// `scaledDt` from `GameSession.tick()` so 2× / 4× / 8× speed-ups
/// affect storms uniformly with everything else.

export type ThunderstormPhase = 'forming' | 'mature' | 'dissipating'

export interface Thunderstorm {
  id: string
  /// World pixel position, world-centered (0,0 at radar centre).
  x: number
  y: number
  /// Compass-degree drift heading. 0 = north, 90 = east.
  directionDeg: number
  /// Current radius in NM (animates with phase).
  sizeNM: number
  /// Mature-phase radius (3–8 NM at spawn).
  baseSizeNM: number
  /// Current drift speed in knots (animates with phase).
  speedKts: number
  /// Mature-phase speed.
  baseSpeedKts: number
  /// Sim-seconds since spawn. Engine increments each tick.
  ageSec: number
  /// Sim-seconds at which the next ±5–10° course jitter fires.
  nextDirectionChangeAtSec: number
  phase: ThunderstormPhase
  /// Total lifespan in sim seconds. Storm is removed at age ≥ this.
  /// Difficulty-scaled at spawn time (Novice 5 min → Master 10 min).
  maxLifespanSec: number
}

/// Lerp the size + speed and return the new phase for a given age.
/// Pure function — no side effects, easy to unit-test.
export function deriveStormState(
  baseSizeNM: number,
  baseSpeedKts: number,
  ageSec: number,
  maxLifespanSec: number,
): { phase: ThunderstormPhase; sizeNM: number; speedKts: number } {
  const ratio = ageSec / maxLifespanSec
  if (ratio < 0.2) {
    // Forming: size 50% → 100%, speed 70% → 100%.
    const t = ratio / 0.2
    return {
      phase: 'forming',
      sizeNM: baseSizeNM * (0.5 + 0.5 * t),
      speedKts: baseSpeedKts * (0.7 + 0.3 * t),
    }
  }
  if (ratio < 0.8) {
    return { phase: 'mature', sizeNM: baseSizeNM, speedKts: baseSpeedKts }
  }
  if (ratio < 1.0) {
    // Dissipating: size 100% → 0%, speed 100% → 50%.
    const t = (ratio - 0.8) / 0.2
    return {
      phase: 'dissipating',
      sizeNM: baseSizeNM * (1 - t),
      speedKts: baseSpeedKts * (1 - 0.5 * t),
    }
  }
  // Past end of life — caller is expected to remove the storm before
  // calling this, but return sensible values for completeness.
  return { phase: 'dissipating', sizeNM: 0, speedKts: baseSpeedKts * 0.5 }
}

/// Inverse-by-size speed scale matching iOS:
///   3 NM →  20–30 kt
///   5 NM →  15–25 kt
///   7 NM →  12–22 kt
///   8 NM →  10–20 kt
/// Min/max are interpolated linearly from the size ratio (3..8 → 0..1).
export function speedRangeForSize(sizeNM: number): { min: number; max: number } {
  const ratio = (sizeNM - 3) / 5
  return { min: 20 - ratio * 10, max: 30 - ratio * 10 }
}

/// Difficulty multiplier applied to the 5-minute base lifespan.
/// Master storms outlive Novice ones by 2× — they linger and force
/// rerouting decisions even after they've moved away from threats.
export function lifespanMultiplierFor(difficulty: string): number {
  switch (difficulty) {
    case 'Novice':
      return 1.0
    case 'Trainee':
      return 1.3
    case 'Licensed':
      return 1.6
    case 'Master':
      return 2.0
    default:
      return 1.3
  }
}
